import { MetricTriggerType, ThresholdOperator, UnifiedAlertSource } from '@logz-pkg/enums';
import { AlertModel, MetricAlertModel } from './legacy/alert';
import { LogTrigger, MetricQueryDefinition, MetricQueryModel, MetricTrigger, SubComponentModel } from './sub-component';
import { CONDITION_REF_ID } from './grafana-alert.manipulator';
function createLogAlert({ filters, query, accountsToQueryOn } = {}) {
    const alert = new AlertModel();
    const subComponent = new SubComponentModel();
    if (filters) {
        subComponent.queryDefinition.filters = filters;
    }
    if (query) {
        subComponent.queryDefinition.query = query;
    }
    if (accountsToQueryOn) {
        subComponent.queryDefinition.accountsToQueryOn = accountsToQueryOn;
    }
    alert.subComponents = [subComponent];
    return alert;
}
function createMetricAlert({ promql, dataSource, queries, mathExpression, } = {}) {
    const alert = new MetricAlertModel();
    alert.trigger = new MetricTrigger();
    if (queries?.length) {
        alert.queries = queries.map(({ refId, promql: queryPromql, dataSource: queryDataSource }) => {
            const query = new MetricQueryModel();
            query.refId = refId;
            query.queryDefinition = new MetricQueryDefinition();
            query.queryDefinition.promql = queryPromql;
            query.queryDefinition.dataSource = queryDataSource;
            return query;
        });
        alert.trigger.triggerType = MetricTriggerType.MATH;
        alert.trigger.mathExpression = mathExpression ?? '';
    }
    else {
        const defaultQuery = new MetricQueryModel();
        defaultQuery.refId = 'A';
        defaultQuery.queryDefinition = new MetricQueryDefinition();
        if (promql) {
            defaultQuery.queryDefinition.promql = promql;
        }
        if (dataSource) {
            defaultQuery.queryDefinition.dataSource = dataSource;
        }
        alert.queries = [defaultQuery];
        alert.trigger.triggerType = MetricTriggerType.THRESHOLD;
        alert.trigger.metricOperator = ThresholdOperator.ABOVE;
        alert.trigger.threshold = 0;
    }
    alert.conditionRefId = CONDITION_REF_ID;
    alert.title = '';
    alert.output.recipients.emails = [];
    alert.output.recipients.notificationEndpoints = [];
    alert.rca = false;
    alert.output.suppressNotificationsMinutes = 0;
    return alert;
}
export function createNewAlert(source, params) {
    /* eslint-enable no-redeclare */
    if (source === UnifiedAlertSource.Metrics) {
        return createMetricAlert(params);
    }
    return createLogAlert(params);
}
function instantiateLogAlert(data) {
    const alert = Object.assign(new AlertModel(), data);
    if (data.subComponents && Array.isArray(data.subComponents)) {
        alert.subComponents = data.subComponents.map((sc) => {
            const subComponent = new SubComponentModel();
            if (sc.queryDefinition) {
                Object.assign(subComponent.queryDefinition, sc.queryDefinition);
            }
            if (sc.trigger) {
                const trigger = new LogTrigger();
                Object.assign(trigger, sc.trigger);
                if (sc.trigger.severityThresholdTiers) {
                    trigger.severityThresholdTiers = Array.isArray(sc.trigger.severityThresholdTiers)
                        ? sc.trigger.severityThresholdTiers
                        : Object.entries(sc.trigger.severityThresholdTiers).map(([level, threshold]) => ({
                            severityLevel: level,
                            threshold: threshold,
                        }));
                }
                subComponent.trigger = trigger;
            }
            if (sc.output) {
                Object.assign(subComponent.output, sc.output);
            }
            return subComponent;
        });
    }
    else {
        alert.subComponents = [new SubComponentModel()];
    }
    return alert;
}
function instantiateMetricAlert(rawData) {
    if (!rawData)
        return createMetricAlert();
    const alert = Object.assign(new MetricAlertModel(), rawData);
    alert.conditionRefId = 'logz_condition';
    const conditionBlock = rawData.data?.find((entry) => entry.datasourceUid === '__expr__' && entry.model?.refId === alert.conditionRefId);
    const trigger = new MetricTrigger();
    if (conditionBlock?.model) {
        const { model } = conditionBlock;
        const condition = model.conditions?.[0];
        trigger.triggerType = model.type === 'math' ? MetricTriggerType.MATH : MetricTriggerType.THRESHOLD;
        if (model.expression) {
            trigger.mathExpression = model.expression;
        }
        if (condition?.evaluator?.type) {
            const { type } = condition.evaluator;
            const params = condition.evaluator.params || [];
            switch (type) {
                case 'gt':
                    trigger.metricOperator = ThresholdOperator.ABOVE;
                    trigger.threshold = params[0] ?? 0;
                    break;
                case 'lt':
                    trigger.metricOperator = ThresholdOperator.BELOW;
                    trigger.threshold = params[0] ?? 0;
                    break;
                case 'within_range':
                    trigger.metricOperator = ThresholdOperator.WITHIN_RANGE;
                    trigger.minThreshold = params[0] ?? 0;
                    trigger.maxThreshold = params[1] ?? 0;
                    break;
                case 'outside_range':
                    trigger.metricOperator = ThresholdOperator.OUTSIDE_RANGE;
                    trigger.minThreshold = params[0] ?? 0;
                    trigger.maxThreshold = params[1] ?? 0;
                    break;
                default:
                    trigger.metricOperator = ThresholdOperator.ABOVE;
                    trigger.threshold = params[0] ?? 0;
            }
        }
    }
    alert.trigger = trigger;
    alert.queries = rawData.data
        .filter((entry) => entry.datasourceUid !== '__expr__')
        .map((entry) => {
        const query = new MetricQueryModel();
        query.refId = entry.refId || 'A';
        const definition = new MetricQueryDefinition();
        definition.dataSource = entry.datasourceUid;
        definition.promql = entry.model?.expr ?? '';
        query.queryDefinition = definition;
        return query;
    });
    return alert;
}
export function instantiateAlert(data) {
    if (typeof data === 'string' && Object.values(UnifiedAlertSource).includes(data)) {
        return createNewAlert(data);
    }
    if (data.source === UnifiedAlertSource.Metrics) {
        return instantiateMetricAlert(data);
    }
    return instantiateLogAlert(data);
}
//# sourceMappingURL=alert-factory.service.js.map